import { Route } from '@angular/router';
import { AuthGuard } from 'app/core/auth/guards/auth.guard';
import { NoAuthGuard } from 'app/core/auth/guards/noAuth.guard';
import { LayoutComponent } from 'app/layout/layout.component';
import { InitialDataResolver } from 'app/app.resolvers';
import { AdminAuthGuard } from './core/auth/guards/admin-auth.guard';
import { AuditorAuthGuard } from './core/auth/guards/auditor-auth.guard';
import { CustomerAuthGuard } from './core/auth/guards/customer-auth.guard';
import { SalesRepAuthGuard } from './core/auth/guards/sales-rep-auth.guard';
import { ClientAuthGuard } from './core/auth/guards/client-auth.guard';
import { MmrAuthGuard } from './core/auth/guards/mmr-auth.guard';

export const appRoutes: Route[] = [
    { path: '', pathMatch: 'full', redirectTo: 'home' },
    { path: 'signed-in-redirect', pathMatch: 'full', redirectTo: 'home' },
    {
        path: '',
        canActivate: [NoAuthGuard],
        canActivateChild: [NoAuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        children: [
            {
                path: 'confirmation-required',
                loadChildren: () => import('app/modules/auth/confirmation-required/confirmation-required.module').then(m => m.AuthConfirmationRequiredModule)
            },
            {
                path: 'forgot-password',
                loadChildren: () => import('app/modules/auth/forgot-password/forgot-password.module').then(m => m.AuthForgotPasswordModule)
            },
            {
                path: 'reset-password',
                loadChildren: () => import('app/modules/auth/reset-password/reset-password.module').then(m => m.AuthResetPasswordModule)
            },
            {
                path: 'sign-in',
                loadChildren: () => import('app/modules/auth/sign-in/sign-in.module').then(m => m.AuthSignInModule)
            },
            {
                path: 'sign-up',
                loadChildren: () => import('app/modules/auth/sign-up/sign-up.module').then(m => m.AuthSignUpModule)
            }
        ]
    },
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        resolve: {
            // initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'sign-out',
                loadChildren: () => import('app/modules/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule)
            }
        ]
    },
    {
        path: '',
        canActivate: [AuthGuard],
        canActivateChild: [AuthGuard],
        component: LayoutComponent,
        data: {
            layout: 'dense'
            // layout: 'empty'
        },
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [
            {
                path: 'home',
                loadChildren: () => import('app/modules/dashboard/dashboard.module').then(m => m.DashboardModule)
            },
            {
                path: 'sign-out',
                loadChildren: () => import('app/modules/auth/sign-out/sign-out.module').then(m => m.AuthSignOutModule)
            },
            {
                path: 'unlock-session',
                loadChildren: () => import('app/modules/auth/unlock-session/unlock-session.module').then(m => m.AuthUnlockSessionModule)
            },
            {
                path: 'profile',
                loadChildren: () => import('app/modules/auth/profile/profile.module').then(m => m.ProfileModule)
            },
            {
                path: 'change-password',
                loadChildren: () => import('app/modules/auth/security/security.module').then(m => m.SecurityModule)
            },
            {
                path: 'auditor',
                canActivate: [AuthGuard, AuditorAuthGuard],
                canActivateChild: [AuthGuard, AuditorAuthGuard],
                loadChildren: () => import('app/modules/auditor/auditor.module').then(m => m.AuditorModule)
            },
            {
                path: 'customer',
                canActivate: [AuthGuard, CustomerAuthGuard],
                canActivateChild: [AuthGuard, CustomerAuthGuard],
                loadChildren: () => import('app/modules/customers/customers.module').then(m => m.CustomersModule)
            },
            {
                path: 'client',
                canActivate: [AuthGuard, ClientAuthGuard],
                canActivateChild: [AuthGuard, ClientAuthGuard],
                loadChildren: () => import('app/modules/client/client.module').then(m => m.ClientModule)
            },
            {
                path: 'mmr',
                canActivate: [AuthGuard, MmrAuthGuard],
                canActivateChild: [AuthGuard, MmrAuthGuard],
                children: [
                    {
                        path: '',
                        loadChildren: () => import('app/modules/mmr/mmr.module').then(m => m.MmrModule)
                    },
                    {
                        path: 'sales',
                        loadChildren: () => import('app/modules/admin/sales/sales.module').then(m => m.SalesModule),
                        data: {
                            isMmr: true
                        }
                    },
                    {
                        path: 'applications',
                        loadChildren: () => import('app/modules/applications/applications.module').then(m => m.ApplicationsModule),
                        data: {
                            isMmr: true
                        }
                    },
                    {
                        path: 'quotes',
                        loadChildren: () => import('app/modules/quote/quote.module').then(m => m.QuoteModule),
                        data: {
                            isMmr: true
                        }
                    },
                    {
                        path: 'audits',
                        loadChildren: () => import('app/modules/audits/audits.module').then(m => m.AuditsModule),
                        data: {
                            isMmr: true
                        }
                    },
                ]
            },
            {
                path: 'shared',
                loadChildren: () => import('app/shared/components/audit/audit.module').then(m => m.AuditModule)
            },
        ]
    },
    {
        path: 'admin',
        canActivate: [AuthGuard, AdminAuthGuard],
        canActivateChild: [AuthGuard, AdminAuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: 'dense'
        },
        children: [
            {
                path: 'personnel',
                loadChildren: () => import('app/modules/admin/personnel/personnel.module').then(m => m.AdminPersonnelModule)
            },
            {
                path: 'settings',
                loadChildren: () => import('app/modules/admin/settings/settings.module').then(m => m.SettingsModule)
            },
            {
                path: 'activity',
                loadChildren: () => import('app/modules/admin/activity/activity.module').then(m => m.AdminActivityModule)
            },
            {
                path: 'clients',
                loadChildren: () => import('app/modules/clients/clients.module').then(m => m.ClientsModule)
            },
            {
                path: 'quotes',
                loadChildren: () => import('app/modules/quote/quote.module').then(m => m.QuoteModule)
            },
            {
                path: 'applications',
                loadChildren: () => import('app/modules/applications/applications.module').then(m => m.ApplicationsModule)
            },
            {
                path: 'audits',
                loadChildren: () => import('app/modules/audits/audits.module').then(m => m.AuditsModule)
            },
            {
                path: 'billing',
                loadChildren: () => import('app/modules/billing/billing.module').then(m => m.BillingModule)
            },
            {
                path: 'file-manager',
                loadChildren: () => import('app/modules/admin/media/file-manager/file-manager.module').then(m => m.FileManagerModule)
            },
            {
                path: 'documents',
                loadChildren: () => import('app/modules/documents/documents.module').then(m => m.DocumentsModule)
            },
            {
                path: 'document-templates',
                loadChildren: () => import('app/modules/audit-document-templates/audit-document-templates.module').then(m => m.AuditDocumentTemplatesModule)
            },
            {
                path: 'reports',
                loadChildren: () => import('app/modules/reports/reports.module').then(m => m.ReportsModule)
            },
            {
                path: 'calendar',
                loadChildren: () => import('app/modules/calendar/calendar.module').then(m => m.CalendarMainModule)
            },
            {
                path: 'sales',
                loadChildren: () => import('app/modules/admin/sales/sales.module').then(m => m.SalesModule)
            },
            {
                path: 'email-manager',
                loadChildren: () => import('app/modules/admin/email-manager/email-manager.module').then(m => m.EmailManagerModule)
            },
            {
                path: 'invoice',
                loadChildren: () => import('app/modules/invoice/invoice.module').then(m => m.InvoiceModule)
            }
        ]
    },
    {
        path: 'sales-rep',
        canActivate: [AuthGuard, SalesRepAuthGuard],
        canActivateChild: [AuthGuard, SalesRepAuthGuard],
        component: LayoutComponent,
        resolve: {
            initialData: InitialDataResolver,
        },
        data: {
            layout: 'dense'
        },
        children: [
            {
                path: 'clients',
                loadChildren: () => import('app/modules/salesrep/clients/clients.module').then(m => m.ClientsModule)
            },
            {
                path: 'sales',
                loadChildren: () => import('app/modules/salesrep/sales/sales.module').then(m => m.SalesModule)
            },
        ]
    },
    {
        path: '',
        component: LayoutComponent,
        data: {
            layout: 'empty'
        },
        resolve: {
            initialData: InitialDataResolver,
        },
        children: [

            {
                path: 'public',
                loadChildren: () => import('app/modules/landing/pre-audit/pre-audit.module').then(m => m.LandingPreAuditModule)
            },
            {
                path: 'request-quote',
                loadChildren: () => import('app/modules/landing/request-quote/request-quote.module').then(m => m.LandingRequestQuoteModule)
            },
            {
                path: 'reload',
                loadChildren: () => import('app/modules/landing/reload/reload.module').then(m => m.ReloadModule)
            },
            {
                path: 'downloads',
                loadChildren: () => import('app/modules/landing/downloads/downloads.module').then(m => m.DownloadsModule)
            },
            {
                path: 'certificate',
                loadChildren: () => import('app/modules/landing/download-client-certificate/download-client-certificate.module').then(m => m.DownloadsClientCertificateModule)
            },
            {
                path: 'error', children: [
                    { path: '403', loadChildren: () => import('app/modules/error/error-403/error-403.module').then(m => m.Error403Module) },
                    { path: '404', loadChildren: () => import('app/modules/error/error-404/error-404.module').then(m => m.Error404Module) },
                    { path: '500', loadChildren: () => import('app/modules/error/error-500/error-500.module').then(m => m.Error500Module) }
                ]
            },
        ]
    },

];
